import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Sidebar from './components/Sidebar/Sidebar';

import LessonsPage from './components/LessonsPage';
import CalendarPage from './components/Calendar/CalendarPage';
import LessonEditorPage from './components/LessonEditor/LessonEditorPage';
import Header from './components/Header';
import LoginForm from './components/LoginForm';
import RegisterForm from './components/RegisterForm';
import LandingPage from './components/LandingPage';
import ProtectedRoute from './components/ProtectedRoute';

import AccountPage from './components/Account/AccountPage';
import ClassesPage from './components/Classes/ClassesPage';
import ClassDetailPage from './components/Classes/ClassDetailPage';
import PrintSummaryPage from './components/Classes/PrintSummaryPage';
import PrivacyPolicyPage from './components/LegalPages/PrivacyPolicyPage';
import TermsOfServicePage from './components/LegalPages/TermsOfServicePage';
import CookiePolicyPage from './components/LegalPages/CookiePolicyPage';
import DashboardPage from './components/Dashboard/DashboardPage';
import WelcomePage from './components/Welcome/WelcomePage';
import ClassOnboardPage from './components/ClassOnboard/ClassOnboardPage';
import LessonWizardPage from './components/LessonWizard/LessonWizardPage';
import WorksheetWizardPage from './components/WorksheetWizard/WorksheetWizardPage';
import WorksheetEditorPage from './components/WorksheetEditor/WorksheetEditorPage';
import WorksheetPrintView from './components/WorksheetEditor/WorksheetPrintView';
import TemplatesPage from './components/Templates/TemplatesPage';
import TemplateBuilderPage from './components/Templates/TemplateBuilderPage';
import WorksheetListPage from './components/WorksheetList/WorksheetListPage';
import { GenerationProvider } from './contexts/GenerationContext';
import GenerationProgressBanner from './components/GenerationProgress/GenerationProgressBanner';

function AppLayout({ children }) {
  const { user } = useAuth();
  if (!user) return <>{children}</>;
  return (
    <div style={{ display: 'flex' }}>
      <Sidebar />
      <main style={{ flex: 1, minWidth: 0 }}>{children}</main>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <GenerationProvider>
      <Router>
        <div className="App">
          <Header />
          <AppLayout>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginForm />} />
            <Route path="/register" element={<RegisterForm />} />
            <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/welcome" element={<ProtectedRoute><WelcomePage /></ProtectedRoute>} />
            <Route
              path="/classes"
              element={
                <ProtectedRoute>
                  <ClassesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/classes/new"
              element={
                <ProtectedRoute>
                  <ClassOnboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/classes/:id"
              element={
                <ProtectedRoute>
                  <ClassDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/classes/:id/summary"
              element={
                <ProtectedRoute>
                  <PrintSummaryPage />
                </ProtectedRoute>
              }
            />
            <Route path="/school-years" element={<Navigate to="/classes" replace />} />
            <Route path="/school-years/:year" element={<Navigate to="/classes" replace />} />
            <Route
              path="/calendar"
              element={
                <ProtectedRoute>
                  <CalendarPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lessons"
              element={
                <ProtectedRoute>
                  <LessonsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lessons/new"
              element={
                <ProtectedRoute>
                  <LessonWizardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lessons/:id/edit"
              element={
                <ProtectedRoute>
                  <LessonEditorPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lessons/:id"
              element={
                <ProtectedRoute>
                  <LessonEditorPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/worksheets"
              element={
                <ProtectedRoute>
                  <WorksheetListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/worksheets/new"
              element={
                <ProtectedRoute>
                  <WorksheetWizardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/worksheets/:id/edit"
              element={
                <ProtectedRoute>
                  <WorksheetEditorPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/worksheets/:id/print"
              element={
                <ProtectedRoute>
                  <WorksheetPrintView />
                </ProtectedRoute>
              }
            />
            <Route
              path="/templates"
              element={
                <ProtectedRoute>
                  <TemplatesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/templates/new"
              element={
                <ProtectedRoute>
                  <TemplateBuilderPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/templates/:templateId/edit"
              element={
                <ProtectedRoute>
                  <TemplateBuilderPage />
                </ProtectedRoute>
              }
            />
            <Route path="/chat" element={<Navigate to="/dashboard" replace />} />
            <Route path="/onboarding" element={<Navigate to="/classes/new" replace />} />
            <Route path="/coverage" element={<Navigate to="/classes" replace />} />
            <Route
              path="/account"
              element={
                <ProtectedRoute>
                  <AccountPage />
                </ProtectedRoute>
              }
            />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/cookies" element={<CookiePolicyPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </AppLayout>
          <GenerationProgressBanner />
        </div>
      </Router>
      </GenerationProvider>
    </AuthProvider>
  );
}

export default App;
